import { useEffect } from 'react';
import Box from '@mui/material/Box';
import { useLocation, useNavigate } from 'react-router';
import Button from '@mui/material/Button';
import {
    AGENCY_ROUTE,
    AGENDA_ROUTE,
    BIG_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    BIG_CLIENT_ADMIN,
    BIG_OPERATOR,
    BIG_SUPER_ADMIN,
    DEVIS_ROUTE,
    MAP_ROUTE,
    NAVIGATION_ROUTE,
    ROLE_BIG_READ_AGENDA,
    ROLE_BIG_READ_QUOTE,
    USERS_ROUTE,
} from '@/utils/CONST';
import { map } from 'lodash';
import { LocalesMenuButton, useLocaleState, useTranslate } from 'react-admin';
import { usePermissions } from 'react-admin';
import useLogout from '@/hooks/useLogout';
import { IconButton, Skeleton } from '@mui/material';
import { Logout, PersonOutlined, Translate } from '@mui/icons-material';
import useGetCurrentUser from '@/hooks/useGetCurrentUser';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tooltip from '@mui/material/Tooltip';
import ScrollableTopBar from '@/components/common/top-bar/scrollable-top-bar';
import * as localization from 'devextreme/localization';
import esMessages from 'devextreme/localization/messages/es.json';
import enMessages from 'devextreme/localization/messages/en.json';
import frMessages from 'devextreme/localization/messages/fr.json';

const paths = (permissions: any) => [
    {
        label: 'routes.navigation',
        path: `/${NAVIGATION_ROUTE}`,
    },
    ...(!permissions?.includes(BIG_AGENCY_MANAGER)
        ? [
              {
                  label: 'routes.users',
                  path: `/${USERS_ROUTE}`,
              },
          ]
        : []),
    ...((permissions?.includes(BIG_SUPER_ADMIN) ||
        permissions?.includes(BIG_ADMIN) ||
        permissions?.includes(BIG_AGENCY_OPERATOR) ||
        permissions?.includes(BIG_AGENCY_MANAGER))
        ? [
              {
                  label: 'routes.agency',
                  path: `/${AGENCY_ROUTE}`,
              },
          ]
        : []),
    // ...(permissions?.includes(ROLE_BIG_READ_CONTRACT_TYPE)
    //     ? [
    //           {
    //               label: 'contract.types.title',
    //               path: `/${CONTRACTS_TYPES_ROUTE}`,
    //           },
    //       ]
    //     : []),
    ...(permissions?.includes(ROLE_BIG_READ_AGENDA)
        ? [
              {
                  label: 'main.navigation.agenda',
                  path: `/${AGENDA_ROUTE}`,
              },
          ]
        : []),
    ...(permissions?.includes(ROLE_BIG_READ_QUOTE) &&
    (permissions?.includes(BIG_ADMIN) || permissions?.includes(BIG_OPERATOR) || permissions?.includes(BIG_CLIENT_ADMIN))
        ? [
              {
                  label: 'routes.devis',
                  path: `/${DEVIS_ROUTE}`,
              },
          ]
        : []),
    ...((permissions?.includes(BIG_SUPER_ADMIN) ||
        permissions?.includes(BIG_ADMIN) ||
        permissions?.includes(BIG_OPERATOR))
        ? [
              {
                  label: 'main.navigation.map',
                  path: `/${MAP_ROUTE}`,
              },
          ]
        : []),
];

const devExtremeMessages: any = {
    es: esMessages,
    en: enMessages,
    fr: frMessages,
};

const TopBar = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const logout = useLogout();
    const { currentUser, isLoading: isLoadingUser } = useGetCurrentUser();
    const locale = useLocaleState();

    useEffect(() => {
        if (locale?.[0]) {
            localization.loadMessages(devExtremeMessages[locale[0]]);
            localization.locale(locale[0]);
            
        }
    }, [locale]);

    const navigateTo = (path: string) => {
        navigate(path);
    };

    const isViewportLessThanLG = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
    const isViewportLessThanXL = useMediaQuery((theme: any) => theme.breakpoints.down('xl'));

    return (
        <Box
            sx={{
                backgroundColor: (theme: any) => theme.palette.custom.nav,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    height: '100%',
                    width: 145,
                    bgcolor: 'white',
                    left: 0,
                    clipPath: 'polygon(0 0, 80% 0, 100% 100%, 0 100%)',
                }}
            />
            <Box
                sx={{
                    height: 35,
                    mr: 5,
                    ml: 1,
                    zIndex: 30,
                }}
                component='img'
                src={'/images/big-logo-white.png'}
                alt={'mainLogo'}
            />

            {isViewportLessThanLG ? (
                <ScrollableTopBar paths={paths} permissions={permissions}>
                    {map(paths(permissions), ({ label, path }) => (
                        <Button
                            key={path}
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                px: 2,
                                flexShrink: 0, // Prevent shrinking of button size
                                scrollSnapAlign: 'start', // Snaps buttons to the start of viewport
                                ...(`/${pathname.split('/')[1]}` === path && {
                                    border: () => `1px solid white`,
                                }),
                            }}
                            onClick={() => navigateTo(path)}
                        >
                            {translate(label)}
                        </Button>
                    ))}
                </ScrollableTopBar>
            ) : (
                <>
                    {map(paths(permissions), ({ label, path }) => (
                        <Button
                            key={path}
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                px: 2,
                                ...(`/${pathname.split('/')[1]}` === path && {
                                    outline: () => `1px solid white`,
                                }),
                            }}
                            onClick={() => navigateTo(path)}
                        >
                            {translate(label)}
                        </Button>
                    ))}
                </>
            )}

            <Box sx={{ right: 4, position: 'absolute', display: 'flex', gap: 0.5 }}>
                <Box
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <LocalesMenuButton
                        languages={[
                            { locale: 'en', name: 'English' },
                            { locale: 'fr', name: 'Français' },
                        ]}
                        icon={<Translate />}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        borderRadius: 2,
                        backgroundColor: 'white',
                        p: 1,
                        px: 2,
                        gap: 1,
                        alignItems: 'center',
                    }}
                >
                    {isLoadingUser ? (
                        <Typography>
                            <Skeleton width={150} />
                        </Typography>
                    ) : (
                        <Tooltip title={`${currentUser?.lastName}, ${currentUser?.firstName}`}>
                            {isViewportLessThanLG ? (
                                <PersonOutlined />
                            ) : (
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        maxWidth: isViewportLessThanXL ? 150 : 300,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {currentUser?.lastName}, {currentUser?.firstName}
                                </Typography>
                            )}
                        </Tooltip>
                    )}
                    <IconButton onClick={() => logout()} title={translate('ra.auth.logout')}>
                        <Logout fontSize={'small'} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default TopBar;
